import React, { useEffect, useState, useCallback } from "react";
import {
  Row, Col, Card, Button, Typography, notification, Form, Input,
  InputNumber, Select, DatePicker, Modal, message, Avatar, Space,
  Divider, Tag, Spin, Tooltip, Alert
} from "antd";
import axios from "axios";
import { useHistory, useParams } from "react-router-dom";
import { baseUrl } from "../../config";
import {
  PhoneOutlined, MoneyCollectOutlined,
  UserOutlined, CheckCircleOutlined, PlusOutlined,
  ArrowLeftOutlined, SearchOutlined,
  BankOutlined, InfoCircleOutlined
} from "@ant-design/icons";
import moment from "moment";
import userDummy from "../../assets/images/dummy-user.png";

const { Title, Text } = Typography;
const { Item } = Form;
const { Option } = Select;

// Quick amount presets
const QUICK_AMOUNTS = [100, 200, 500, 1000, 2000, 5000];

const PAYMENT_MODES = [
  { value: "UPI",           label: "UPI" },
  { value: "Paytm",         label: "Paytm" },
  { value: "PhonePe",       label: "PhonePe" },
  { value: "Gpay",          label: "GPay" },
  { value: "Bank Transfer", label: "Bank Transfer / NEFT / RTGS" },
  { value: "Cash",          label: "Cash" },
  { value: "Cheque",        label: "Cheque" },
  { value: "Other",         label: "Other" },
];

const PAYMENT_CATEGORIES = [
  { value: "ShikshaNidi",  label: "Shiksha Nidi / शिक्षानीदी" },
  { value: "Gosala",       label: "Gosala / गौशाला" },
  { value: "Birthday",     label: "Birthday / जन्मदिन" },
  { value: "Marriage",     label: "Marriage / शादी" },
  { value: "Other",        label: "Other / अन्य" },
];

// ── Member Info Card ───────────────────────────────────────────────────────────
const MemberCard = ({ member, lastPayment, loading }) => {
  if (loading) {
    return (
      <Card style={{ borderRadius: 12, border: "2px dashed #d9d9d9", minHeight: 180 }} bodyStyle={{ padding: 24 }}>
        <div style={{ textAlign: "center", paddingTop: 40 }}><Spin tip="Looking up member…" /></div>
      </Card>
    );
  }

  if (!member) {
    return (
      <Card style={{ borderRadius: 12, border: "2px dashed #d9d9d9", minHeight: 180, background: "#fafafa" }} bodyStyle={{ padding: 24 }}>
        <div style={{ textAlign: "center", color: "#bfbfbf", paddingTop: 30 }}>
          <UserOutlined style={{ fontSize: 36, marginBottom: 8 }} />
          <div style={{ fontSize: 14 }}>Enter phone number to load member details</div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      style={{ borderRadius: 12, border: "2px solid #1890ff", background: "linear-gradient(135deg,#e6f7ff,#f0f5ff)" }}
      bodyStyle={{ padding: "18px 20px" }}
    >
      <Row gutter={[16, 0]} align="middle">
        <Col>
          <Avatar
            size={70}
            src={member.profile_img || userDummy}
            icon={<UserOutlined />}
            style={{ border: "3px solid #1890ff" }}
          />
        </Col>
        <Col flex={1}>
          <Text strong style={{ fontSize: 17, display: "block", lineHeight: 1.3 }}>
            {member.name_hn || member.name_eng}
          </Text>
          {member.name_hn && (
            <Text type="secondary" style={{ fontSize: 13, display: "block" }}>{member.name_eng}</Text>
          )}
          <Space style={{ marginTop: 4 }} wrap>
            <Tag color="blue" style={{ borderRadius: 20 }}>{member.phone_no}</Tag>
            {member.status && (
              <Tag color={member.status === "Active" ? "green" : "red"} style={{ borderRadius: 20 }}>
                {member.status}
              </Tag>
            )}
          </Space>
        </Col>
      </Row>

      <Divider style={{ margin: "12px 0" }} />

      <Row gutter={[12, 8]}>
        {(member.per_village_hn || member.per_village_en) && (
          <Col xs={24} sm={12}>
            <Text type="secondary" style={{ fontSize: 11 }}>Village</Text>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {member.per_village_hn || member.per_village_en}
            </div>
          </Col>
        )}
        {(member.per_district_hn || member.per_district_en) && (
          <Col xs={24} sm={12}>
            <Text type="secondary" style={{ fontSize: 11 }}>District</Text>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {member.per_district_hn || member.per_district_en}
            </div>
          </Col>
        )}
        {member.announced_amount && (
          <Col xs={24} sm={12}>
            <Text type="secondary" style={{ fontSize: 11 }}>Announced Amount</Text>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#52c41a" }}>
              ₹{Number(member.announced_amount).toLocaleString("en-IN")}
            </div>
          </Col>
        )}
        {member.announced_type && (
          <Col xs={24} sm={12}>
            <Text type="secondary" style={{ fontSize: 11 }}>Frequency</Text>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{member.announced_type}× per year</div>
          </Col>
        )}
      </Row>

      {/* Last Payment */}
      {lastPayment && (
        <>
          <Divider style={{ margin: "12px 0" }} />
          <div style={{ background: "#fff", borderRadius: 8, padding: "10px 14px" }}>
            <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>Last Payment</Text>
            <Row justify="space-between" align="middle">
              <Text strong style={{ fontSize: 16, color: "#52c41a" }}>
                ₹{Number(lastPayment.last_payment_amount || lastPayment.amount || 0).toLocaleString("en-IN")}
              </Text>
              <Tag color="default" style={{ borderRadius: 20, fontSize: 11 }}>
                {lastPayment.payment_entry_date
                  ? moment(lastPayment.payment_entry_date).format("DD MMM YYYY")
                  : lastPayment.transaction_date
                  ? moment(lastPayment.transaction_date).format("DD MMM YYYY")
                  : "—"}
              </Tag>
            </Row>
          </div>
        </>
      )}

      <div style={{ marginTop: 8, textAlign: "right" }}>
        <Tag color={member.id ? "success" : "warning"} icon={<CheckCircleOutlined />}>
          {member.id ? "Existing Member" : "New Member — will be created"}
        </Tag>
      </div>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

function PaymentsForm() {
  const { id } = useParams();
  const history = useHistory();
  const [form] = Form.useForm();

  const isUpdateMode = !!id;

  const [member, setMember]           = useState(null);
  const [lastPayment, setLastPayment] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);
  const [phoneNumber, setPhoneNumber]   = useState("");
  const [successModal, setSuccessModal] = useState(false);
  const [successData, setSuccessData]   = useState(null);

  // Set default dates on mount
  useEffect(() => {
    if (!isUpdateMode) {
      form.setFieldsValue({
        transaction_date:   moment(),
        payment_entry_date: moment(),
        payment_status:     "Success",
        payment_virify:     "pending",
        bank_account:       "1",
        country_code:       "91",
      });
    }
  }, [form, isUpdateMode]);

  // Load existing payment for edit mode
  useEffect(() => {
    if (id) {
      fetchPaymentDetails();
    }
  }, [id]);

  const fetchPaymentDetails = async () => {
    try {
      const resp = await axios.get(`${baseUrl}/payments/read/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
      });
      if (resp.data.status !== false) {
        const d = resp.data.result;
        d.transaction_date   = d.transaction_date   ? moment(d.transaction_date)   : null;
        d.payment_entry_date = d.payment_entry_date ? moment(d.payment_entry_date) : null;
        d.start_month        = d.start_month        ? moment(d.start_month)        : null;
        d.end_month          = d.end_month          ? moment(d.end_month)          : null;
        form.setFieldsValue(d);
        if (d.payment_type === "Recurring") setShowRecurring(true);
        if (d.phone_no) {
          setPhoneNumber(d.phone_no);
          lookupMember(d.phone_no, false);
        }
      }
    } catch (err) {
      notification.error({ message: "Error", description: "Failed to load payment details" });
    }
  };

  // ── Member lookup ────────────────────────────────────────────────────────────
  const lookupMember = useCallback(async (phone, showMsg = true) => {
    if (!phone || phone.length < 10) return;
    setLookupLoading(true);
    setMember(null);
    setLastPayment(null);
    try {
      const resp = await axios.post(
        `${baseUrl}/payments/checkUserPhone`,
        { phone_no: phone },
        { headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` } }
      );
      if (resp.data.exists) {
        const details = resp.data.details;
        setMember(details);
        // Auto-fill recurring fields
        if (details.announced_amount) {
          form.setFieldsValue({
            amount_announce: details.announced_amount,
            duration_type:   details.announced_type,
          });
        }
        // Fetch last payment
        try {
          const lpResp = await axios.get(
            `${baseUrl}/payments/get-last-payment?userId=${details.id}`,
            { headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` } }
          );
          if (lpResp.data?.result?.[0]) setLastPayment(lpResp.data.result[0]);
        } catch (_) {}
        if (showMsg) message.success(`Member found: ${details.name_eng || details.name_hn}`);
      } else {
        setMember({ isNew: true });
        if (showMsg) message.info("New member — will be auto-created on payment save.");
      }
    } catch (err) {
      message.error("Phone lookup failed");
    } finally {
      setLookupLoading(false);
    }
  }, [form]);

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 12);
    setPhoneNumber(val);
    form.setFieldsValue({ phone_no: val });
    if (val.length >= 10) lookupMember(val);
    else { setMember(null); setLastPayment(null); }
  };

  // ── Submit ───────────────────────────────────────────────────────────────────
  const onFinish = async (values) => {
    setSubmitLoading(true);
    try {
      const formData = new FormData();
      Object.keys(values).forEach(key => {
        let v = values[key];
        if (v && v._isAMomentObject) v = v.format("YYYY-MM-DD");
        if (v !== undefined && v !== null) formData.append(key, v);
      });

      const url = isUpdateMode
        ? `${baseUrl}/payments/update/${id}`
        : `${baseUrl}/payments/create`;
      const method = isUpdateMode ? "patch" : "post";

      const resp = await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
      });

      if (resp.status === 200) {
        if (isUpdateMode) {
          notification.success({ message: "Updated!", description: "Payment updated successfully." });
          history.push("/payments");
        } else {
          setSuccessData({ ...values, memberName: member?.name_eng || member?.name_hn || "Member" });
          setSuccessModal(true);
        }
      } else {
        notification.warning({ message: "Warning", description: resp.data.message });
      }
    } catch (err) {
      notification.error({ message: "Error", description: err.response?.data?.message || "Failed to save payment" });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleAddAnother = () => {
    form.resetFields();
    form.setFieldsValue({
      transaction_date:   moment(),
      payment_entry_date: moment(),
      payment_status:     "Success",
      payment_virify:     "pending",
      bank_account:       "1",
      country_code:       "91",
    });
    setMember(null);
    setLastPayment(null);
    setPhoneNumber("");
    setShowRecurring(false);
    setSuccessModal(false);
    setSuccessData(null);
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: "0 0 40px" }}>
      {/* Header */}
      <Row align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Button icon={<ArrowLeftOutlined />} onClick={() => history.push("/payments")} style={{ marginRight: 12, borderRadius: 8 }}>
            Back
          </Button>
        </Col>
        <Col flex={1}>
          <Title level={3} style={{ margin: 0 }}>
            {isUpdateMode ? "Edit Payment" : "Add Payment / भुगतान जोड़ें"}
          </Title>
          <Text type="secondary">
            {isUpdateMode ? "Update existing payment record" : "Enter member's phone number to begin"}
          </Text>
        </Col>
      </Row>

      <Form form={form} onFinish={onFinish} layout="vertical" scrollToFirstError>
        <Row gutter={[24, 24]}>

          {/* ── LEFT: Form ────────────────────────────────────────────────────── */}
          <Col xs={24} lg={16}>

            {/* Section 1: Member Identification */}
            <Card
              style={{ borderRadius: 14, marginBottom: 20, border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.07)" }}
              bodyStyle={{ padding: "20px 24px" }}
              title={
                <Space>
                  <PhoneOutlined style={{ color: "#1890ff" }} />
                  <span style={{ fontWeight: 600 }}>Member Identification</span>
                </Space>
              }
            >
              <Row gutter={[16, 0]} align="bottom">
                <Col xs={24} sm={14}>
                  <Item label="Mobile Number / फोन नंबर" name="phone_no"
                    rules={[
                      { required: true, message: "Phone number is required" },
                      { pattern: /^\d{10,12}$/, message: "Enter 10–12 digit number" },
                    ]}
                  >
                    <Input
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      placeholder="Enter 10-digit mobile number"
                      prefix={<PhoneOutlined style={{ color: "#1890ff" }} />}
                      addonBefore={
                        <Select defaultValue="91" style={{ width: 70 }}
                          onChange={v => form.setFieldsValue({ country_code: v })}>
                          <Option value="91">+91</Option>
                          <Option value="1">+1</Option>
                          <Option value="44">+44</Option>
                        </Select>
                      }
                      addonAfter={
                        <Button
                          icon={<SearchOutlined />}
                          onClick={() => lookupMember(phoneNumber)}
                          loading={lookupLoading}
                          type="text"
                          style={{ border: "none", padding: "0 8px" }}
                        >
                          Lookup
                        </Button>
                      }
                      maxLength={12}
                      size="large"
                    />
                  </Item>
                </Col>
                <Col xs={24} sm={10}>
                  <Item label="Payment Type / भुगतान प्रकार" name="payment_type">
                    <Select
                      placeholder="Select type"
                      size="large"
                      onChange={v => setShowRecurring(v === "Recurring")}
                    >
                      <Option value="OneTime">One Time / एक बार</Option>
                      <Option value="Recurring">Recurring / पुनरावर्ती</Option>
                    </Select>
                  </Item>
                </Col>
              </Row>

              {/* Recurring fields */}
              {showRecurring && (
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Item label="Duration / समर्पण अवधि (per year)" name="duration_type">
                      <Select placeholder="Times per year" size="large">
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(n => (
                          <Option key={n} value={n}>{n}× per year</Option>
                        ))}
                      </Select>
                    </Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Item label="Announced Amount / घोषित राशि (₹)" name="amount_announce"
                      rules={[{ required: true, message: "Required for recurring" }]}>
                      <InputNumber style={{ width: "100%" }} size="large"
                        prefix="₹" placeholder="Announced amount"
                        formatter={v => v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""}
                        parser={v => v.replace(/,/g, "")}
                      />
                    </Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Item label="Period Start Month" name="start_month">
                      <DatePicker picker="month" style={{ width: "100%" }} size="large"
                        format="MMM YYYY" placeholder="Start month" />
                    </Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Item label="Period End Month" name="end_month">
                      <DatePicker picker="month" style={{ width: "100%" }} size="large"
                        format="MMM YYYY" placeholder="End month" />
                    </Item>
                  </Col>
                </Row>
              )}
            </Card>

            {/* Section 2: Payment Details */}
            <Card
              style={{ borderRadius: 14, marginBottom: 20, border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.07)" }}
              bodyStyle={{ padding: "20px 24px" }}
              title={
                <Space>
                  <MoneyCollectOutlined style={{ color: "#52c41a" }} />
                  <span style={{ fontWeight: 600 }}>Payment Details / भुगतान विवरण</span>
                </Space>
              }
            >
              {/* Quick Amount Buttons */}
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
                  Quick Amount / त्वरित राशि
                </Text>
                <Space wrap>
                  {QUICK_AMOUNTS.map(amt => (
                    <Button
                      key={amt}
                      size="small"
                      style={{ borderRadius: 20, fontWeight: 600 }}
                      onClick={() => form.setFieldsValue({ amount: amt })}
                    >
                      ₹{amt.toLocaleString("en-IN")}
                    </Button>
                  ))}
                </Space>
              </div>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12} lg={8}>
                  <Item label="Amount / राशि (₹)" name="amount"
                    rules={[{ required: true, message: "Amount is required" }]}>
                    <InputNumber
                      style={{ width: "100%" }} size="large"
                      placeholder="Enter amount"
                      min={1}
                      formatter={v => v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""}
                      parser={v => v.replace(/,/g, "")}
                      prefix={<MoneyCollectOutlined />}
                    />
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Item label="Payment Category / श्रेणी" name="payment_category">
                    <Select placeholder="Select category" size="large">
                      {PAYMENT_CATEGORIES.map(c => (
                        <Option key={c.value} value={c.value}>{c.label}</Option>
                      ))}
                    </Select>
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Item label="Payment Mode / माध्यम" name="pyament_mode_type">
                    <Select placeholder="Select mode" size="large">
                      {PAYMENT_MODES.map(m => (
                        <Option key={m.value} value={m.value}>{m.label}</Option>
                      ))}
                    </Select>
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Item label="Transaction Date / लेन-देन तिथि" name="transaction_date">
                    <DatePicker style={{ width: "100%" }} size="large"
                      format="DD-MM-YYYY" placeholder="Select date" />
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Item label="Entry Date / प्रविष्टि तिथि" name="payment_entry_date">
                    <DatePicker style={{ width: "100%" }} size="large"
                      format="DD-MM-YYYY" placeholder="Select date" />
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Item label={
                    <Space>
                      UTR / Transaction ID
                      <Tooltip title="UPI Transaction Reference / Bank UTR number">
                        <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                      </Tooltip>
                    </Space>
                  } name="utr_number">
                    <Input size="large" placeholder="Enter UTR / Ref No." />
                  </Item>
                </Col>
              </Row>
            </Card>

            {/* Section 3: Status & Bank */}
            <Card
              style={{ borderRadius: 14, border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.07)" }}
              bodyStyle={{ padding: "20px 24px" }}
              title={
                <Space>
                  <BankOutlined style={{ color: "#722ed1" }} />
                  <span style={{ fontWeight: 600 }}>Status & Account / स्थिति</span>
                </Space>
              }
            >
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12} lg={6}>
                  <Item label="Payment Status" name="payment_status"
                    rules={[{ required: true, message: "Required" }]}>
                    <Select size="large" placeholder="Select status">
                      <Option value="Success">✅ Success</Option>
                      <Option value="Pending">⏳ Pending</Option>
                      <Option value="Failed">❌ Failed</Option>
                    </Select>
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Item label="Verification Status" name="payment_virify"
                    rules={[{ required: true, message: "Required" }]}>
                    <Select size="large" placeholder="Select">
                      <Option value="verified">✓ Verified</Option>
                      <Option value="pending">⏳ Pending</Option>
                      <Option value="failed">✗ Failed</Option>
                    </Select>
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Item label="Bank Account" name="bank_account">
                    <Select size="large" placeholder="Select account">
                      <Option value="1">Account 1 (Main)</Option>
                      <Option value="2">Account 2</Option>
                    </Select>
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Item label="Transaction ID (optional)" name="transaction_id">
                    <Input size="large" placeholder="Internal Txn ID" />
                  </Item>
                </Col>
              </Row>
            </Card>

          </Col>

          {/* ── RIGHT: Member Card ─────────────────────────────────────────────── */}
          <Col xs={24} lg={8}>
            <div style={{ position: "sticky", top: 80 }}>
              <div style={{ marginBottom: 12 }}>
                <Text strong style={{ fontSize: 14, color: "#555" }}>
                  <UserOutlined style={{ marginRight: 6 }} />
                  Member Info / सदस्य जानकारी
                </Text>
              </div>
              <MemberCard member={member} lastPayment={lastPayment} loading={lookupLoading} />

              {/* Tips */}
              {!isUpdateMode && (
                <Alert
                  style={{ marginTop: 16, borderRadius: 10 }}
                  type="info"
                  showIcon
                  message="Tips"
                  description={
                    <ul style={{ paddingLeft: 16, margin: "4px 0", fontSize: 12 }}>
                      <li>Member details auto-load when you enter the phone number</li>
                      <li>Announced amount & frequency pre-filled from member profile</li>
                      <li>Today's date is pre-selected for transaction & entry date</li>
                    </ul>
                  }
                />
              )}
            </div>
          </Col>
        </Row>

        {/* Submit Row */}
        <Row style={{ marginTop: 24 }}>
          <Col>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitLoading}
                size="large"
                icon={isUpdateMode ? <CheckCircleOutlined /> : <PlusOutlined />}
                style={{ borderRadius: 10, minWidth: 160, height: 46 }}
              >
                {isUpdateMode ? "Update Payment" : "Save Payment"}
              </Button>
              <Button
                size="large"
                onClick={() => history.push("/payments")}
                style={{ borderRadius: 10, height: 46 }}
              >
                Cancel
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>

      {/* ── Success Modal ────────────────────────────────────────────────────── */}
      <Modal
        open={successModal}
        onCancel={() => { setSuccessModal(false); history.push("/payments"); }}
        footer={null}
        centered
        width={460}
        closable={false}
      >
        <div style={{ textAlign: "center", padding: "24px 16px" }}>
          <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
          <Title level={3} style={{ margin: "0 0 8px" }}>Payment Saved!</Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 6 }}>
            {successData?.memberName}
          </Text>
          <Text strong style={{ fontSize: 22, color: "#52c41a", display: "block", marginBottom: 20 }}>
            ₹{Number(successData?.amount || 0).toLocaleString("en-IN")}
          </Text>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Button
              type="primary"
              block
              size="large"
              icon={<PlusOutlined />}
              style={{ borderRadius: 10 }}
              onClick={handleAddAnother}
            >
              Add Another Payment
            </Button>
            <Button
              block
              size="large"
              style={{ borderRadius: 10 }}
              onClick={() => history.push("/payments")}
            >
              Go to Payments List
            </Button>
          </Space>
        </div>
      </Modal>
    </div>
  );
}

export default PaymentsForm;
